import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { RotateCcw, X } from "lucide-react";

interface GlobalFilterPopoverProps {
  filters: {
    search: string;
    categories: string[];
    tags: string[];
    difficulty: string[];
    estimatedTimeMin: number | undefined;
    estimatedTimeMax: number | undefined;
    priceMin: number | undefined;
    priceMax: number | undefined;
    vendorId: string | undefined;
  };
  onFilterChange: (filters: any) => void;
  availableCategories: any[];
  availableTags: any[];
  availableVendors: any[];
}

const GlobalFilterPopover = ({
  filters,
  onFilterChange,
  availableCategories,
  availableTags,
  availableVendors,
}: GlobalFilterPopoverProps) => {
  const { t, i18n } = useTranslation();

  // Helper to get localized name
  const getLocalizedName = (name: any): string => {
    if (typeof name === 'string') return name;
    if (typeof name === 'object' && name !== null) {
      const currentLang = i18n.language || 'en';
      if (currentLang === 'he' && name.he) return name.he;
      if (currentLang === 'de' && name.de) return name.de;
      return name.en || name.he || name.de || '';
    }
    return '';
  };

  const handleCategoryToggle = (categoryId: string) => {
    const newCategories = filters.categories.includes(categoryId)
      ? filters.categories.filter((c) => c !== categoryId)
      : [...filters.categories, categoryId];
    onFilterChange({ ...filters, categories: newCategories });
  };

  const handleTagToggle = (tagId: string) => {
    const newTags = filters.tags.includes(tagId)
      ? filters.tags.filter((t) => t !== tagId)
      : [...filters.tags, tagId];
    onFilterChange({ ...filters, tags: newTags });
  };

  const handleDifficultyToggle = (difficulty: string) => {
    const newDifficulty = filters.difficulty.includes(difficulty)
      ? filters.difficulty.filter((d) => d !== difficulty)
      : [...filters.difficulty, difficulty];
    onFilterChange({ ...filters, difficulty: newDifficulty });
  };

  const handleTimeRangeChange = (range: string) => {
    let timeValues: { min?: number; max?: number } = {};
    switch (range) {
      case "0-30":
        timeValues = { max: 30 };
        break;
      case "30-60":
        timeValues = { min: 30, max: 60 };
        break;
      case "60-120":
        timeValues = { min: 60, max: 120 };
        break;
      case "120+":
        timeValues = { min: 120 };
        break;
    }
    onFilterChange({
      ...filters,
      estimatedTimeMin: timeValues.min,
      estimatedTimeMax: timeValues.max,
    });
  };

  const handlePriceRangeChange = (range: string) => {
    let priceValues: { min?: number; max?: number } = {};
    switch (range) {
      case "free":
        priceValues = { max: 0 };
        break;
      case "0-10":
        priceValues = { min: 0.01, max: 10 };
        break;
      case "10-25":
        priceValues = { min: 10, max: 25 };
        break;
      case "25-50":
        priceValues = { min: 25, max: 50 };
        break;
      case "50+":
        priceValues = { min: 50 };
        break;
    }
    onFilterChange({
      ...filters,
      priceMin: priceValues.min,
      priceMax: priceValues.max,
    });
  };

  const handleClearFilters = () => {
    onFilterChange({
      search: "",
      categories: [],
      tags: [],
      difficulty: [],
      estimatedTimeMin: undefined,
      estimatedTimeMax: undefined,
      priceMin: undefined,
      priceMax: undefined,
      vendorId: undefined,
    });
  };

  const activeFilterCount =
    filters.categories.length +
    filters.tags.length +
    filters.difficulty.length +
    (filters.vendorId ? 1 : 0) +
    (filters.estimatedTimeMin !== undefined || filters.estimatedTimeMax !== undefined ? 1 : 0) +
    (filters.priceMin !== undefined || filters.priceMax !== undefined ? 1 : 0);

  // Get current time range string
  const getCurrentTimeRange = (): string => {
    if (filters.estimatedTimeMin === undefined && filters.estimatedTimeMax === 30) return "0-30";
    if (filters.estimatedTimeMin === 30 && filters.estimatedTimeMax === 60) return "30-60";
    if (filters.estimatedTimeMin === 60 && filters.estimatedTimeMax === 120) return "60-120";
    if (filters.estimatedTimeMin === 120 && filters.estimatedTimeMax === undefined) return "120+";
    return "";
  };

  // Get current price range string
  const getCurrentPriceRange = (): string => {
    if (filters.priceMin === undefined && filters.priceMax === 0) return "free";
    if (filters.priceMin === 0.01 && filters.priceMax === 10) return "0-10";
    if (filters.priceMin === 10 && filters.priceMax === 25) return "10-25";
    if (filters.priceMin === 25 && filters.priceMax === 50) return "25-50";
    if (filters.priceMin === 50 && filters.priceMax === undefined) return "50+";
    return "";
  };

  return (
    <>
      {/* Fixed Header */}
      <div className="p-4 pb-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm">{t("filter_options") || "Filters"}</h4>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="h-8 px-2 text-xs"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              {t("clear_all") || "Clear"}
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto flex-1 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            {/* Categories */}
            {availableCategories.length > 0 && (
              <div className="space-y-2">
                <div className="font-medium text-sm">{t("categories") || "Categories"}</div>
                <div className="flex flex-wrap gap-2">
                  {availableCategories.map((category) => (
                    <Button
                      key={category._id}
                      variant={filters.categories.includes(category._id) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleCategoryToggle(category._id)}
                      className="h-8 text-xs"
                    >
                      <div
                        className="w-2 h-2 rounded-full mr-1.5"
                        style={{ backgroundColor: category.color || "#6B7280" }}
                      />
                      {getLocalizedName(category.name)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {availableTags.length > 0 && (
              <div className="space-y-2">
                <div className="font-medium text-sm">{t("tags") || "Tags"}</div>
                <div className="flex flex-wrap gap-2">
                  {availableTags.map((tag) => (
                    <Button
                      key={tag._id}
                      variant={filters.tags.includes(tag._id) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleTagToggle(tag._id)}
                      className="h-8 text-xs"
                    >
                      <div
                        className="w-2 h-2 rounded-full mr-1.5"
                        style={{ backgroundColor: tag.color || "#F3F4F6" }}
                      />
                      {getLocalizedName(tag.name)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Vendor */}
            {availableVendors.length > 0 && (
              <div className="space-y-2">
                <div className="font-medium text-sm">{t("vendor") || "Vendor"}</div>
                <div className="flex flex-wrap gap-2">
                  {availableVendors.map((vendor) => (
                    <Button
                      key={vendor._id}
                      variant={filters.vendorId === vendor._id ? "default" : "outline"}
                      size="sm"
                      onClick={() =>
                        onFilterChange({
                          ...filters,
                          vendorId: filters.vendorId === vendor._id ? undefined : vendor._id,
                        })
                      }
                      className="h-8 text-xs"
                    >
                      {vendor.displayName}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {/* Difficulty */}
            <div className="space-y-2">
              <div className="font-medium text-sm">{t("difficulty") || "Difficulty"}</div>
              <div className="grid grid-cols-2 gap-2">
                {["beginner", "intermediate", "advanced"].map((level) => (
                  <Button
                    key={level}
                    variant={filters.difficulty.includes(level) ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleDifficultyToggle(level)}
                    className="h-8 text-xs"
                  >
                    {t(level) || level.charAt(0).toUpperCase() + level.slice(1)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Time Range */}
            <div className="space-y-2">
              <div className="font-medium text-sm">{t("time") || "Time"}</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: "0-30", label: t("under_30_min") || "< 30 min" },
                  { value: "30-60", label: t("30_60_min") || "30-60 min" },
                  { value: "60-120", label: t("1_2_hours") || "1-2 hrs" },
                  { value: "120+", label: t("over_2_hours") || "2+ hrs" },
                ].map((option) => (
                  <Button
                    key={option.value}
                    variant={getCurrentTimeRange() === option.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleTimeRangeChange(option.value)}
                    className="h-8 text-xs"
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div className="space-y-2">
              <div className="font-medium text-sm">{t("price") || "Price"}</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: "free", label: t("free") || "Free" },
                  { value: "0-10", label: "$0-$10" },
                  { value: "10-25", label: "$10-$25" },
                  { value: "25-50", label: "$25-$50" },
                  { value: "50+", label: "$50+" },
                ].map((option) => (
                  <Button
                    key={option.value}
                    variant={getCurrentPriceRange() === option.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePriceRangeChange(option.value)}
                    className="h-8 text-xs"
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GlobalFilterPopover;
